import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { ADD_BEIHILFE_LEGACY_HEADER_TOKEN } from '../../interceptors';
import { APP_VERSION } from '../../types';
import { AntragDtoModel, Compatibility, DocumentInfo, StaticFileContent, VersionChecker } from '../../models';
import { AntragSupportingDataServerResponse } from '../../interfaces';
import { ApiService } from '../api';

@Injectable()
export class ResourceProvider {
  private readonly api = inject(ApiService);
  private readonly _versionToken = inject(APP_VERSION);

  // todo backend caching
  public getAntragStates(): Observable<AntragSupportingDataServerResponse> {
    return this.api.restGet<AntragSupportingDataServerResponse>('data/supporting-data').pipe(shareReplay(1));
  }

  /**
   * Retrieves all Anträge from the backend.
   *
   * @param {boolean} [legacyContext=false] - Whether to include the legacy context header.
   * @return {Observable<AntragDtoModel[]>} An observable emitting an array of AntragsInfo.
   */
  public getAntragsHistory(legacyContext = false): Observable<AntragDtoModel[]> {
    if (legacyContext) {
      return this.api.restGet<AntragDtoModel[]>('data/antraege', {
        context: new HttpContext().set(ADD_BEIHILFE_LEGACY_HEADER_TOKEN, true),
      });
    }
    return this.api.restGet<AntragDtoModel[]>('data/antraege');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getApplicationById(applicationsId: string): Observable<any> {
    return this.api.restGet(`data/antraege/${applicationsId}`);
  }

  public getContainersList<T>(): Observable<T> {
    return this.api.restGet<T>('data/antraege/containers');
  }

  public getContainerById<T>(uuid: string): Observable<T> {
    return this.api.restGet<T>(`data/antraege/containers/${uuid}`);
  }

  public getAvailability(): Observable<VersionChecker> {
    return this.api
      .restGet<{ releaseVersion: string; compatibility: Compatibility }>('utils/status', {
        params: new HttpParams().set('clientVersion', this._versionToken.version),
      })
      .pipe(
        map((response) => new VersionChecker(response.releaseVersion, response.compatibility)),
        catchError(() => {
          return of(
            new VersionChecker('2.5.0', {
              serverVersion: '2.5.0',
              isCompatible: true,
            }),
          );
        }),
      );
  }

  public getFinishedClaimById(rGuid: string): Observable<Blob> {
    // todo test if we can remove responseType
    return this.api.restGet(`data/documents/${rGuid}`, { responseType: 'blob' });
  }

  public getFinishedClaims(): Observable<DocumentInfo[]> {
    return this.getDocumentInfoArray() as Observable<DocumentInfo[]>;
  }

  public getGenericCustomerPage(pageName: string): Observable<StaticFileContent> {
    return this.api.restGet<StaticFileContent>(`file/${pageName}`);
  }

  public getSingleAntragFromHistory(antragsId: string): Observable<AntragDtoModel> {
    return this.getAntragsHistory(true).pipe(
      filter((data) => data && data.length > 0),
      switchMap((antrage: AntragDtoModel[]) => of(antrage.find((antrag) => antrag.id === antragsId))),
      filter((antrag): antrag is AntragDtoModel => antrag !== undefined),
    );
  }

  public setDownloadSuccess(rGuid: string, setNewPinSuccess: boolean): Observable<boolean> {
    return this.api.post('data/viewed', {
      rGuid,
      setNewPinSuccess,
    });
  }

  public setEncryptionSuccess(antragsSeiteId: string): Observable<boolean> {
    return this.api.post('data/decrypted', {
      id: antragsSeiteId,
    });
  }

  private getDocumentInfoArray(): Observable<DocumentInfo[]> {
    return this.api.restGet('data/documents').pipe(map((value) => value)) as Observable<DocumentInfo[]>;
  }
}
